import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import { Star } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import Eyebrow from "@/components/common/Eyebrow";

export default function ReviewDialog({ booking, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (comment.trim().length < 10) { toast("Please write at least a sentence"); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", { booking_id: booking.id, rating, comment });
      toast("Thank you — your review is published.");
      setOpen(false);
      onSubmitted?.();
    } catch (err) {
      toast(err.response?.data?.detail || "Could not submit review");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Star size={12} />}
        onClick={() => setOpen(true)}
        data-testid={`review-btn-${booking.id}`}
        sx={{ fontSize: 12 }}
      >
        Leave a review
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography className="font-serif-display" sx={{ fontSize: 24 }}>Share your stay</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{booking.villa_name}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={submit} sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
            <Box>
              <Eyebrow sx={{ mb: 1.5 }}>Your rating</Eyebrow>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <IconButton
                    type="button"
                    key={n}
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    data-testid={`star-${n}`}
                    size="small"
                    sx={{ transition: "transform 0.15s", "&:hover": { transform: "scale(1.1)" } }}
                  >
                    <Star size={28} className={(hover || rating) >= n ? "text-gold" : ""} fill={(hover || rating) >= n ? "currentColor" : "none"} color={(hover || rating) >= n ? undefined : "currentColor"} />
                  </IconButton>
                ))}
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>{rating} / 5</Typography>
              </Box>
            </Box>
            <Box>
              <Eyebrow sx={{ mb: 1 }}>Your note</Eyebrow>
              <TextField
                required
                fullWidth
                multiline
                rows={5}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What made this stay special?"
                inputProps={{ minLength: 10, "data-testid": "review-comment" }}
              />
            </Box>
            <Button type="submit" variant="contained" fullWidth disabled={submitting} data-testid="review-submit">
              {submitting ? "Publishing…" : "Publish review"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
